import { useState, useEffect, useCallback } from 'react';

// Custom hook for localStorage with JSON serialization
export const useLocalStorage = (key, initialValue) => {
  // Get value from localStorage or use initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when state changes
  const setValue = useCallback((value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      setStoredValue(prevValue => {
        const valueToStore = value instanceof Function ? value(prevValue) : value;
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        return valueToStore;
      });
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key]);

  // Remove item from localStorage
  const removeValue = () => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue, removeValue];
};

// Hook specifically for quiz progress
export const useQuizProgress = () => {
  const [progress, setProgress, clearProgress] = useLocalStorage('doshaQuizProgress', {
    currentStep: 0,
    answers: {},
    startedAt: null,
    lastSavedAt: null
  });

  const saveProgress = useCallback((stepIndex, answers) => {
    setProgress(prevProgress => ({
      currentStep: stepIndex,
      answers,
      startedAt: prevProgress.startedAt || new Date().toISOString(),
      lastSavedAt: new Date().toISOString()
    }));
  }, [setProgress]);

  const hasProgress = () => {
    return progress.startedAt && Object.keys(progress.answers).length > 0;
  };

  const getProgressPercentage = (totalSteps) => {
    if (!totalSteps) return 0;
    return Math.round((progress.currentStep / totalSteps) * 100);
  };

  return {
    progress,
    saveProgress,
    clearProgress,
    hasProgress,
    getProgressPercentage
  };
};

// Hook for saving quiz results
export const useQuizResults = () => {
  const [results, setResults, clearResults] = useLocalStorage('doshaQuizResults', []);

  const saveResult = (result) => {
    const newResult = {
      ...result,
      id: Date.now().toString(),
      completedAt: new Date().toISOString()
    };
    
    setResults(prevResults => [newResult, ...prevResults.slice(0, 4)]); // Keep last 5 results
    return newResult;
  };

  const getLatestResult = () => {
    return results.length > 0 ? results[0] : null;
  };

  const hasResults = () => {
    return results.length > 0;
  };

  return {
    results,
    saveResult,
    clearResults,
    getLatestResult,
    hasResults
  };
};

export default useLocalStorage;
